import React, { useState } from 'react';
import { XMLParser } from 'fast-xml-parser';
import { auth, db } from './firebase'; // Asegúrate de que 'auth' esté importado si lo usas directamente.
import { collection, addDoc, getDocs, query, where, doc, setDoc, getDoc } from 'firebase/firestore';
import './FacturasApp.css';

const FacturasApp = () => {
  const [resumen, setResumen] = useState([]);
  
  const getCurrentUserEmail = () => {
    try {
      return auth.currentUser?.email || 'sistema@defecto.com';
    } catch (error) {
      console.log('Error obteniendo usuario:', error);
      return 'sistema@empresa.com';
    }
  };

  return (
    <div className="facturas-app">
      <div className="app-content">
        <div className="card">
          <SubirFacturas setResumen={setResumen} getCurrentUserEmail={getCurrentUserEmail} />
        </div>
        
        <div className="card">
          <ResumenFacturas resumen={resumen} />
        </div>
      </div>
    </div>
  );
};

function SubirFacturas({ setResumen, getCurrentUserEmail }) {
  const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);

  function removeUndefinedFields(obj) {
    if (!obj || typeof obj !== 'object') {
      return {};
    }
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== undefined && value !== null)
    );
  }

  // Helper: extrae el Codigo del CodigoComercial, manejando tanto objeto único como array (múltiples CodigoComercial)
  const obtenerCodigoComercial = (codigoComercial) => {
    if (!codigoComercial) return null;
    if (Array.isArray(codigoComercial)) {
      // Preferir Tipo="01" (código del proveedor); si no existe, usar el primero
      const tipo01 = codigoComercial.find(cc => cc.Tipo === '01');
      return tipo01?.Codigo || codigoComercial[0]?.Codigo || null;
    }
    return codigoComercial.Codigo || null;
  };

  const obtenerIdentificacionEmpresa = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Parametros'));
      const config = snapshot.docs[0]?.data();
      return config?.identificacion?.trim() || '';
    } catch (error) {
      console.error('Error obteniendo identificación de empresa:', error);
      return '';
    }
  };

  const determinarTipoDocumento = (parsedXml) => {
    if (parsedXml.FacturaElectronica) return 'FacturaElectronica';
    if (parsedXml.NotaCreditoElectronica) return 'NotaCreditoElectronica';
    if (parsedXml.NotaDebitoElectronica) return 'NotaDebitoElectronica';
    if (parsedXml.TiqueteElectronico) return 'TiqueteElectronico';
    return null;
  };

  const existeEnCatalogoCompras = async (emisorNumeroIdentificacion, codigoComercialCodigo) => {
    try {
      const q = query(
        collection(db, 'SicCatalogoCompras'),
        where('emisorNumeroIdentificacion', '==', emisorNumeroIdentificacion),
        where('codigoComercialCodigo', '==', codigoComercialCodigo),
        where('codigoEmpresa', '==', 'XXXYYYZZZ123')
      );
      
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error verificando existencia en catálogo:', error);
      return false;
    }
  };

  const agregarACatalogoCompras = async (detalle, emisorNumeroIdentificacion, codigoComercialCodigo, codigoEmpresa) => {
    try {
      const existe = await existeEnCatalogoCompras(emisorNumeroIdentificacion, codigoComercialCodigo);
      
      if (!existe && codigoComercialCodigo) {
        const nuevoRegistro = {
          emisorNumeroIdentificacion,
          codigoComercialCodigo,
          detalle: detalle || 'Sin descripción',
          codigoEmpresa: codigoEmpresa,
          fechaCreacion: new Date().toISOString(),
          fechaSubida: new Date().toISOString(),
          fechaModificacion: new Date().toISOString(),
          usuarioResponsable: getCurrentUserEmail()
        };
        
        await addDoc(collection(db, 'SicCatalogoCompras'), nuevoRegistro);
        console.log('Nuevo registro agregado a SicCatalogoCompras');
      }
    } catch (error) {
      console.error('Error agregando a catálogo de compras:', error);
    }
  };

  // Función para agregar un nuevo cliente si no existe
  const agregarCliente = async (clienteData) => {
    if (!clienteData.cedula) {
      console.warn('Intento de agregar cliente sin cédula.');
      return;
    }

    const clienteRef = doc(db, 'SicTablaClientes', clienteData.cedula);
    const docSnap = await getDoc(clienteRef);

    if (!docSnap.exists()) { // Si el documento no existe, lo creamos.
      try {
        await setDoc(clienteRef, {
          cedula: clienteData.cedula,
          nombre: clienteData.nombre,
          correo: clienteData.correo || '',
          telefonoOficina: clienteData.telefono || '', // Usamos telefonoOficina para consistencia
          responsable: getCurrentUserEmail(),
          fechacreacion: new Date().toISOString(),
          // El campo ID ya no es necesario, el ID del documento es la cédula.
        });
        console.log('Nuevo cliente agregado:', clienteData.nombre);
      } catch (error) {
        console.error('Error agregando cliente:', error);
      }
    }
  };

  // Función para agregar un nuevo proveedor si no existe
  const agregarProveedor = async (proveedorData) => {
    if (!proveedorData.cedula) {
      console.warn('Intento de agregar proveedor sin cédula.');
      return;
    }

    const proveedorRef = doc(db, 'SicTablaProveedores', proveedorData.cedula);
    const docSnap = await getDoc(proveedorRef);

    if (!docSnap.exists()) { // Si el documento no existe, lo creamos.
      try {
        await setDoc(proveedorRef, {
          cedula: proveedorData.cedula,
          nombre: proveedorData.nombre,
          correo: proveedorData.correo || '',
          telefonoOficina: proveedorData.telefono || '', // Usamos telefonoOficina para consistencia
          responsable: getCurrentUserEmail(),
          fechacreacion: new Date().toISOString(),
          // El campo ID ya no es necesario, el ID del documento es la cédula.
        });
        console.log('Nuevo proveedor agregado:', proveedorData.nombre);
      } catch (error) {
        console.error('Error agregando proveedor:', error);
      }
    }
  };

  // 🎫 Función para procesar Tiquete Electrónico
  const procesarTiqueteElectronico = (documento, identificacionPropia) => {
    const emisor = documento.Emisor || {};
    const receptor = documento.Receptor || {};
    const resumenFactura = documento.ResumenFactura || {};

    const emisorIdentificacion = emisor.Identificacion?.Numero || '';
    const esVenta = emisorIdentificacion === identificacionPropia;
    const tipoFactura = esVenta ? 'Venta' : 'Compra';

    const encabezadoData = {
      clave: documento.Clave,
      numeroConsecutivo: documento.NumeroConsecutivo,
      fechaEmision: documento.FechaEmision,
      condicionVenta: documento.CondicionVenta,
      plazoCredito: documento.PlazoCredito,
      
      emisorNombre: emisor.Nombre,
      emisorNombreComercial: emisor.NombreComercial,
      emisorTipoIdentificacion: emisor.Identificacion?.Tipo,
      emisorNumeroIdentificacion: emisorIdentificacion,
      emisorProvincia: emisor.Ubicacion?.Provincia,
      emisorCanton: emisor.Ubicacion?.Canton,
      emisorDistrito: emisor.Ubicacion?.Distrito,
      emisorBarrio: emisor.Ubicacion?.Barrio,
      emisorOtrasSenas: emisor.Ubicacion?.OtrasSenas,
      emisorTelefonoCodigoPais: emisor.Telefono?.CodigoPais,
      emisorTelefonoNumero: emisor.Telefono?.NumTelefono,
      emisorCorreoElectronico: emisor.CorreoElectronico,
      
      receptorNombre: receptor.Nombre,
      receptorTipoIdentificacion: receptor.Identificacion?.Tipo,
      receptorNumeroIdentificacion: receptor.Identificacion?.Numero,
      receptorCorreoElectronico: receptor.CorreoElectronico,
      
      codigoMoneda: resumenFactura.CodigoTipoMoneda?.CodigoMoneda,
      tipoCambio: resumenFactura.CodigoTipoMoneda?.TipoCambio,
      totalVenta: resumenFactura.TotalVenta,
      totalDescuentos: resumenFactura.TotalDescuentos,
      totalVentaNeta: resumenFactura.TotalVentaNeta,
      totalImpuesto: resumenFactura.TotalImpuesto,
      totalComprobante: resumenFactura.TotalComprobante,
      
      tipoFactura: tipoFactura,
      tipoDocumento: 'TiqueteElectronico',
      responsable: getCurrentUserEmail(),
      fechaSubida: new Date().toISOString(),
    };

    let lineasArray = [];
    if (documento.DetalleServicio && documento.DetalleServicio.LineaDetalle) {
      const lineas = documento.DetalleServicio.LineaDetalle;
      lineasArray = Array.isArray(lineas) ? lineas : [lineas];
    }

    const detallesData = lineasArray.map((linea) => {
      const codigoComercialCodigo = obtenerCodigoComercial(linea.CodigoComercial) || linea.CodigoCABYS || linea.Codigo;
      const codigoEmpresa = esVenta
        ? (codigoComercialCodigo || "XXXYYYZZZ123")
        : "XXXYYYZZZ123";

      return {
        numeroConsecutivo: documento.NumeroConsecutivo,
        tipoFactura: tipoFactura,
        emisorNumeroIdentificacion: emisorIdentificacion,
        emisorNombre: emisor.Nombre,
        receptorNumeroIdentificacion: receptor.Identificacion?.Numero,
        receptorNombre: receptor.Nombre,
        fechaEmision: documento.FechaEmision,
        codigoMoneda: resumenFactura.CodigoTipoMoneda?.CodigoMoneda,
        tipoCambio: resumenFactura.CodigoTipoMoneda?.TipoCambio,
        numeroLinea: linea.NumeroLinea,
        codigo: linea.CodigoCABYS || linea.Codigo,
        codigoComercialTipo: linea.CodigoComercial?.Tipo,
        codigoComercialCodigo: codigoComercialCodigo,
        cantidad: linea.Cantidad,
        unidadMedida: linea.UnidadMedida,
        detalle: linea.Detalle,
        precioUnitario: linea.PrecioUnitario,
        montoTotal: linea.MontoTotal,
        subTotal: linea.SubTotal,
        baseImponible: linea.BaseImponible,
        impuestoCodigo: linea.Impuesto?.Codigo,
        impuestoCodigoTarifa: linea.Impuesto?.CodigoTarifaIVA,
        impuestoTarifa: linea.Impuesto?.Tarifa,
        impuestoMonto: linea.Impuesto?.Monto,
        impuestoNeto: linea.ImpuestoNeto,
        montoTotalLinea: linea.MontoTotalLinea,
        codigoEmpresa: codigoEmpresa,
        responsable: getCurrentUserEmail(),
        fechaSubida: new Date().toISOString(),
      };
    });

    return { encabezadoData, detallesData };
  };

  // 📄 Función para procesar Facturas Electrónicas y Notas
  const procesarFacturaElectronica = (documento, tipoDocumento, identificacionPropia) => {
    const resumenFactura = documento.ResumenFactura || {};
    const moneda = resumenFactura.CodigoTipoMoneda || {};
    const emisor = documento.Emisor || {};
    const receptor = documento.Receptor || {};

    const emisorIdentificacion = emisor.Identificacion?.Numero || '';
    const esVenta = emisorIdentificacion === identificacionPropia;
    
    let tipoFactura;
    switch(tipoDocumento) {
      case 'NotaCreditoElectronica':
        tipoFactura = esVenta ? 'NC Venta' : 'NC Compra';
        break;
      case 'NotaDebitoElectronica':
        tipoFactura = esVenta ? 'ND Venta' : 'ND Compra';
        break;
      default:
        tipoFactura = esVenta ? 'Venta' : 'Compra';
    }

    const encabezadoData = {
      clave: documento.Clave,
      codigoActividad: documento.CodigoActividad,
      condicionVenta: documento.CondicionVenta,
      plazoCredito: documento.PlazoCredito,
      medioPago: documento.MedioPago,
      emisorNombre: emisor.Nombre,
      emisorNombreComercial: emisor.NombreComercial,
      emisorTipoIdentificacion: emisor.Identificacion?.Tipo,
      emisorNumeroIdentificacion: emisorIdentificacion,
      emisorProvincia: emisor.Ubicacion?.Provincia,
      emisorCanton: emisor.Ubicacion?.Canton,
      emisorDistrito: emisor.Ubicacion?.Distrito,
      emisorBarrio: emisor.Ubicacion?.Barrio,
      emisorOtrasSenas: emisor.Ubicacion?.OtrasSenas,
      emisorTelefonoCodigoPais: emisor.Telefono?.CodigoPais,
      emisorTelefonoNumero: emisor.Telefono?.NumTelefono,
      emisorFaxCodigoPais: emisor.Fax?.CodigoPais,
      emisorFaxNumero: emisor.Fax?.NumTelefono,
      emisorCorreoElectronico: emisor.CorreoElectronico,
      receptorNombre: receptor.Nombre,
      receptorNombreComercial: receptor.NombreComercial,
      receptorTipoIdentificacion: receptor.Identificacion?.Tipo,
      receptorNumeroIdentificacion: receptor.Identificacion?.Numero,
      receptorCorreoElectronico: receptor.CorreoElectronico,
      receptorTelefonoNumero: receptor.Telefono?.NumTelefono,
      numeroConsecutivo: documento.NumeroConsecutivo,
      fechaEmision: documento.FechaEmision,
      codigoMoneda: moneda.CodigoMoneda,
      tipoCambio: moneda.TipoCambio,
      totalServGravados: resumenFactura.TotalServGravados,
      totalServExentos: resumenFactura.TotalServExentos,
      totalServExonerado: resumenFactura.TotalServExonerado,
      totalMercanciasGravadas: resumenFactura.TotalMercanciasGravadas,
      totalMercanciasExentas: resumenFactura.TotalMercanciasExentas,
      totalMercExonerada: resumenFactura.TotalMercExonerada,
      totalGravado: resumenFactura.TotalGravado,
      totalExento: resumenFactura.TotalExento,
      totalExonerado: resumenFactura.TotalExonerado,
      totalVenta: resumenFactura.TotalVenta,
      totalDescuentos: resumenFactura.TotalDescuentos,
      totalVentaNeta: resumenFactura.TotalVentaNeta,
      totalImpuesto: resumenFactura.TotalImpuesto,
      totalComprobante: resumenFactura.TotalComprobante,
      tipoFactura: tipoFactura,
      tipoDocumento: tipoDocumento,
      responsable: getCurrentUserEmail(),
      fechaSubida: new Date().toISOString(),
    };

    const lineas = documento.DetalleServicio.LineaDetalle;
    const lineasArray = Array.isArray(lineas) ? lineas : [lineas];

    const detallesData = lineasArray.map((linea) => {
      const codigoComercialCodigo = obtenerCodigoComercial(linea.CodigoComercial) || linea.CodigoCABYS || linea.Codigo;
      const codigoEmpresa = esVenta
        ? (codigoComercialCodigo || "XXXYYYZZZ123")
        : "XXXYYYZZZ123";

      return {
        numeroConsecutivo: documento.NumeroConsecutivo,
        tipoFactura: tipoFactura,
        emisorNumeroIdentificacion: emisorIdentificacion,
        emisorNombre: emisor.Nombre,
        receptorNumeroIdentificacion: receptor.Identificacion?.Numero,
        receptorNombre: receptor.Nombre,
        fechaEmision: documento.FechaEmision,
        codigoMoneda: moneda.CodigoMoneda,
        tipoCambio: moneda.TipoCambio,
        numeroLinea: linea.NumeroLinea,
        codigo: linea.CodigoCABYS || linea.Codigo,
        codigoComercialTipo: linea.CodigoComercial?.Tipo,
        codigoComercialCodigo: codigoComercialCodigo,
        cantidad: linea.Cantidad,
        unidadMedida: linea.UnidadMedida,
        detalle: linea.Detalle,
        precioUnitario: linea.PrecioUnitario,
        montoTotal: linea.MontoTotal,
        subTotal: linea.SubTotal,
        impuestoCodigo: linea.Impuesto?.Codigo,
        impuestoCodigoTarifa: linea.Impuesto?.CodigoTarifa,
        impuestoTarifa: linea.Impuesto?.Tarifa,
        impuestoMonto: linea.Impuesto?.Monto,
        montoTotalLinea: linea.MontoTotalLinea,
        codigoEmpresa: codigoEmpresa,
        responsable: getCurrentUserEmail(),
        fechaSubida: new Date().toISOString(),
      };
    });

    return { encabezadoData, detallesData };
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setArchivosSeleccionados(files);
    
    const parser = new XMLParser({
      ignoreAttributes: false,
      parseTagValue: false,
      parseAttributeValue: false,
    });

    const resultados = [];
    const identificacionPropia = await obtenerIdentificacionEmpresa();

    for (const file of files) {
      try {
        const text = await file.text();
        const nombreArchivo = file.name;

        const parsed = parser.parse(text);
        const tipoDocumento = determinarTipoDocumento(parsed);
        
        if (!tipoDocumento) {
          resultados.push({ archivo: nombreArchivo, estado: 'Ignorado', motivo: 'No es un documento electrónico válido' });
          continue;
        }

        const documento = parsed[tipoDocumento];

        // Verificar estructura básica según el tipo de documento
        if (tipoDocumento === 'TiqueteElectronico') {
          if (!documento.NumeroConsecutivo || !documento.Emisor) {
            resultados.push({ archivo: nombreArchivo, estado: 'Ignorado', motivo: 'Estructura incompleta de tiquete' });
            continue;
          }
        } else if (!documento.DetalleServicio || !documento.ResumenFactura) {
          resultados.push({ archivo: nombreArchivo, estado: 'Ignorado', motivo: 'Estructura incompleta' });
          continue;
        }

        const numeroConsecutivo = documento.NumeroConsecutivo;

        const querySnapshot = await getDocs(collection(db, 'EncabezadoFactura'));
        const yaExiste = querySnapshot.docs.some(
          doc => doc.data().numeroConsecutivo === numeroConsecutivo
        );

        if (yaExiste) {
          resultados.push({ archivo: nombreArchivo, estado: 'Duplicado', motivo: `Ya existe ${numeroConsecutivo}` });
          continue;
        }

        let encabezadoData, detallesData;

        // Procesar según el tipo de documento
        if (tipoDocumento === 'TiqueteElectronico') {
          const resultadoTiquete = procesarTiqueteElectronico(documento, identificacionPropia);
          encabezadoData = resultadoTiquete.encabezadoData;
          detallesData = resultadoTiquete.detallesData;
        } else {
          // Procesar Facturas, Notas de Crédito y Débito
          const resultadoFactura = procesarFacturaElectronica(documento, tipoDocumento, identificacionPropia);
          encabezadoData = resultadoFactura.encabezadoData;
          detallesData = resultadoFactura.detallesData;
        }

        // Después de procesar, agregar cliente o proveedor si corresponde
        if (encabezadoData.tipoFactura.includes('Venta')) {
          await agregarCliente({
            cedula: encabezadoData.receptorNumeroIdentificacion,
            nombre: encabezadoData.receptorNombre,
            correo: encabezadoData.receptorCorreoElectronico,
            telefono: encabezadoData.receptorTelefonoNumero,
          });
        } else if (encabezadoData.tipoFactura.includes('Compra')) {
          await agregarProveedor({
            cedula: encabezadoData.emisorNumeroIdentificacion,
            nombre: encabezadoData.emisorNombre,
            correo: encabezadoData.emisorCorreoElectronico,
            telefono: encabezadoData.emisorTelefonoNumero,
          });
        }


        const encabezadoLimpio = removeUndefinedFields(encabezadoData || {});
        await addDoc(collection(db, 'EncabezadoFactura'), encabezadoLimpio);

        for (const detalle of detallesData) {
          const detalleLimpio = removeUndefinedFields(detalle || {});
          
          await addDoc(collection(db, 'DetalleFactura'), detalleLimpio);
          
          if (detalleLimpio.codigoEmpresa &&
              detalleLimpio.emisorNumeroIdentificacion &&
              detalleLimpio.codigoComercialCodigo) {
            await agregarACatalogoCompras(
              detalleLimpio.detalle,
              detalleLimpio.emisorNumeroIdentificacion,
              detalleLimpio.codigoComercialCodigo,
              detalleLimpio.codigoEmpresa
            );
          }
        }

        resultados.push({ archivo: nombreArchivo, estado: 'Subido', motivo: `${tipoDocumento} ${numeroConsecutivo} almacenada` });

      } catch (error) {
        console.error('Error procesando archivo:', error);
        resultados.push({ archivo: file.name, estado: 'Error', motivo: `Error procesando archivo: ${error.message}` });
      }
    }

    setResumen(resultados);
    setArchivosSeleccionados([]);
  };

  return (
    <div className="subir-facturas">
      <h2>📤 Subir Documentos XML</h2>
      <p>Selecciona archivos XML de facturas o tiquetes electrónicos</p>
      <div className="upload-container">
        <label htmlFor="file-upload" className="custom-file-upload">
          📂 Seleccionar archivos XML
        </label>
        <input
          id="file-upload"
          type="file"
          accept=".xml"
          multiple
          onChange={handleFileUpload}
        />
      </div>
      
      {archivosSeleccionados.length > 0 && (
        <div className="file-selected">
          <strong>Archivos seleccionados:</strong>
          {archivosSeleccionados.map((file, index) => (
            <div key={index}>• {file.name}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResumenFacturas({ resumen }) {
  if (resumen.length === 0) {
    return (
      <div className="resumen-facturas">
        <h2>📊 Resumen de Documentos</h2>
        <div className="empty-state">
          <p>No hay documentos procesados aún.</p>
          <p>Sube algunos archivos XML para comenzar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="resumen-facturas">
      <h2>📊 Resumen de Documentos</h2>
      <div className="summary-table-container">
        <table className="summary-table">
          <thead>
            <tr>
              <th>Archivo</th>
              <th>Estado</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {resumen.map((r, idx) => (
              <tr key={idx} className={r.estado.toLowerCase()}>
                <td>{r.archivo}</td>
                <td>
                  <span className={`status-badge ${r.estado.toLowerCase()}`}>
                    {r.estado}
                  </span>
                </td>
                <td>{r.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FacturasApp;